import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Copy, ImagePlus, MoonStar, Home as HomeIcon, Footprints, DoorOpen, PawPrint, X } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MaskedLines from "../components/MaskedLines";
import api from "../lib/api";

const TIME_SLOTS = ["8 – 10 AM", "10 AM – 12 PM", "12 – 2 PM", "2 – 4 PM", "4 – 6 PM", "6 – 8 PM", "8 – 10 PM"];
const STEP_LABELS = ["Service", "Schedule", "Meet & greet", "Your pet", "Review"];
const PET_TYPES = ["Dog", "Cat", "Other"];

const SERVICE_ICONS = {
  "overnight-house-sitting": MoonStar,
  boarding: HomeIcon,
  "dog-walking": Footprints,
  "drop-in-visits": DoorOpen,
};

const EMPTY_FORM = {
  service_slug: "",
  start_date: "",
  end_date: "",
  start_time: "16:00",
  end_time: "17:00",
  time_slot: "",
  walk_length: "",
  drop_days: [{ date: "", slot_1: "", slot_2: "", third: false, slot_3: "" }],
  pet_name: "",
  dogs_count: 1,
  cats_count: 0,
  pet_breed: "",
  pet_age: "",
  pet_photo: "",
  care_instructions: "",
  owner_name: "",
  owner_email: "",
  owner_phone: "",
  owner_zip: "",
  greet_slots: [
    { date: "", time: "" },
    { date: "", time: "" },
    { date: "", time: "" },
  ],
};

const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const inputCls =
  "w-full rounded-2xl border border-line bg-white px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color,box-shadow] duration-200 focus:border-marmalade focus:outline-none";

const Field = ({ label, error, testid, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">{label}</span>
    <div className="mt-2">{children}</div>
    {error && (
      <span data-testid={`${testid}-error`} className="mt-1.5 block text-xs text-red-600">
        {error}
      </span>
    )}
  </label>
);

const Book = () => {
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [result, setResult] = useState(null);
  const [zipCheck, setZipCheck] = useState(null);

  useEffect(() => {
    const load = (retry = true) =>
      api
        .get("/services")
        .then((r) => {
          setServices(r.data);
          const pre = searchParams.get("service");
          if (pre && r.data.some((s) => s.slug === pre)) {
            setForm((f) => ({ ...f, service_slug: pre, time_slot: r.data.find((s) => s.slug === pre).overnight ? "Overnight" : f.time_slot }));
          }
        })
        .catch(() => {
          // a brief gateway hiccup shouldn't blank the page — try once more
          if (retry) setTimeout(() => load(false), 1500);
          else setServices([]);
        });
    load();
  }, [searchParams]);

  const service = useMemo(
    () => services?.find((s) => s.slug === form.service_slug) || null,
    [services, form.service_slug]
  );

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined, form: undefined }));
  };

  const setDropDay = (i, key, val) =>
    setForm((f) => ({ ...f, drop_days: f.drop_days.map((r, j) => (j === i ? { ...r, [key]: val } : r)) }));
  const addDropDay = () =>
    setForm((f) => ({ ...f, drop_days: [...f.drop_days, { date: "", slot_1: "", slot_2: "", third: false, slot_3: "" }] }));
  const removeDropDay = (i) =>
    setForm((f) => ({ ...f, drop_days: f.drop_days.filter((_, j) => j !== i) }));

  const [petUploading, setPetUploading] = useState(false);
  const pickPetPhoto = async (f) => {
    if (!f) return;
    setPetUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/pet-photo", fd);
      set("pet_photo", data.url);
      toast.success("Pet photo added — thank you!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't upload that photo — try another.");
    } finally {
      setPetUploading(false);
    }
  };

  const pickService = (s) => {
    setForm((f) => ({
      ...f,
      service_slug: s.slug,
      time_slot: s.overnight ? "Overnight" : "",
      end_date: s.overnight ? f.end_date : "",
    }));
    setErrors({});
  };

  const validateStep = (idx) => {
    const e = {};
    if (idx === 0 && !form.service_slug) e.service_slug = "Please pick a service to continue.";
    if (idx === 1) {
      if (service?.slug === "drop-in-visits") {
        form.drop_days.forEach((d, i) => {
          if (!d.date) e[`drop_date_${i}`] = "Pick a date.";
          if (!d.slot_1) e[`drop_v1_${i}`] = "Pick visit 1's window.";
          if (!d.slot_2) e[`drop_v2_${i}`] = "Pick visit 2's window.";
          if (d.third && !d.slot_3) e[`drop_v3_${i}`] = "Pick visit 3's window.";
        });
        if (!form.drop_days.some((d) => d.date)) e.start_date = "Add at least one day for your drop-in visits.";
      } else {
        if (!form.start_date) e.start_date = "Please choose a date.";
        if (service?.overnight) {
          if (!form.end_date) e.end_date = "Please choose an end date.";
          else if (form.end_date < form.start_date) e.end_date = "End date can't be before the start date.";
        } else if (!form.time_slot) {
          e.time_slot = "Please pick a time window.";
        }
      }
    }
    if (idx === 3) {
      if (form.dogs_count + form.cats_count < 1) e.dogs_count = "Tell Ari how many dogs and cats are staying.";
      if (service?.slug === "dog-walking" && form.dogs_count < 1) e.dogs_count = "Walks are for dogs — at least one, please.";
      if (!form.pet_name.trim()) e.pet_name = "Your pets' names help Ari say hello properly.";
      if (!form.care_instructions.trim()) e.care_instructions = "Even a couple of lines helps Ari care well.";
      if (!form.owner_name.trim()) e.owner_name = "Ari needs a name for the confirmation.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email)) e.owner_email = "Please enter a valid email.";
      if (!form.owner_phone.trim()) e.owner_phone = "A phone number helps in case of emergencies.";
      if (!form.owner_zip.trim()) e.owner_zip = "A zip code helps Ari confirm she can reach you.";
      if (zipCheck?.enabled && !zipCheck.ok) e.owner_zip = zipCheck.message;
    }
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep((s) => Math.min(s + 1, 4));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    setErrors({});
    try {
      const payload = { ...form };
      if (service?.slug === "drop-in-visits") {
        const days = form.drop_days.filter((d) => d.date);
        payload.start_date = days[0]?.date || "";
        payload.end_date = days.length > 1 ? days[days.length - 1].date : "";
        payload.time_slot = days.map((d) => `${d.slot_1} & ${d.slot_2}${d.third ? ` & ${d.slot_3}` : ""}`).join("; ");
        payload.drop_in_days = days.map((d) => ({
          date: d.date,
          time_slot: d.slot_1,
          slot_1: d.slot_1,
          slot_2: d.slot_2,
          third_visit: d.third,
          slot_3: d.third ? d.slot_3 : "",
        }));
      }
      const { data } = await api.post("/bookings", payload);
      setResult(data);
      setReference(data.reference);
      toast.success("Request sent — Ari will be in touch shortly.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors({ form: typeof detail === "string" ? detail : "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setStep(0);
    setReference(null);
    setResult(null);
  };

  const setGreet = (i, key, value) => {
    setForm((f) => ({
      ...f,
      greet_slots: f.greet_slots.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
    }));
    setErrors((e) => ({ ...e, form: undefined }));
  };

  const onZipChange = (value) => {
    const v = value.replace(/\D/g, "").slice(0, 5);
    set("owner_zip", v);
    if (v.length === 5) {
      api
        .get(`/service-area/check?zip=${v}`)
        .then((r) => setZipCheck(r.data))
        .catch(() => setZipCheck(null));
    } else {
      setZipCheck(null);
    }
  };

  const estimate = useMemo(() => {
    if (!service) return null;
    const r = service.rates || {};
    const dogs = Number(form.dogs_count) || 0;
    const cats = Number(form.cats_count) || 0;
    if (dogs + cats < 1) return null;
    if (service.overnight && form.start_date && form.end_date) {
      const nights = Math.max(Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000), 1);
      let perNight = 0;
      const parts = [];
      if (dogs >= 1) {
        perNight += r.dog + (dogs - 1) * r.dog_additional;
        parts.push(`${dogs} dog${dogs !== 1 ? "s" : ""} ($${r.dog}/night + $${r.dog_additional}/extra)`);
      }
      if (cats >= 1) {
        perNight += r.cat + (cats - 1) * r.cat_additional;
        parts.push(`${cats} cat${cats !== 1 ? "s" : ""} ($${r.cat}/night + $${r.cat_additional}/extra)`);
      }
      const midday = Boolean(form.end_time) && form.end_time <= "14:30";
      if (midday) {
        return {
          total: (nights - 1) * perNight + perNight / 2,
          note: `${nights} night${nights !== 1 ? "s" : ""} · ${parts.join(" + ")} · midday pick-up — last day half price`,
        };
      }
      return { total: nights * perNight, note: `${nights} night${nights !== 1 ? "s" : ""} · ${parts.join(" + ")}` };
    }
    if (service.slug === "dog-walking") {
      const base = form.walk_length === "60 min" ? r.walk_60 : r.walk_30;
      const extra = Math.max(dogs - 1, 0) * (r.dog_additional || 0);
      return {
        total: base + extra,
        note: `${form.walk_length || "30 min"} walk ($${base})${extra ? ` + $${extra} extra dog` : ""}`,
      };
    }
    if (service.slug === "drop-in-visits") {
      const days = form.drop_days.filter((d) => d.date);
      const perDay =
        (dogs >= 1 ? r.dog + (dogs - 1) * r.dog_additional : 0) +
        (cats >= 1 ? r.cat + (cats - 1) * r.cat_additional : 0);
      const thirds = days.filter((d) => d.third).length;
      const parts = [];
      if (dogs >= 1) parts.push(`${dogs} dog${dogs !== 1 ? "s" : ""} ($${r.dog}/day + $${r.dog_additional}/extra)`);
      if (cats >= 1) parts.push(`${cats} cat${cats !== 1 ? "s" : ""} ($${r.cat}/day + $${r.cat_additional}/extra)`);
      return {
        total: days.length * perDay + thirds * 10,
        note: `${days.length} day${days.length !== 1 ? "s" : ""} · ${parts.join(" + ")} · up to 2 visits/day${
          thirds ? ` + $${thirds * 10} third visit${thirds !== 1 ? "s" : ""}` : ""
        }`,
      };
    }
    let total = 0;
    const parts = [];
    if (dogs >= 1) {
      total += r.dog + (dogs - 1) * r.dog_additional;
      parts.push(`${dogs} dog${dogs !== 1 ? "s" : ""} ($${r.dog}${dogs > 1 ? ` + $${r.dog_additional}/extra` : ""})`);
    }
    if (cats >= 1) {
      total += r.cat + (cats - 1) * r.cat_additional;
      parts.push(`${cats} cat${cats !== 1 ? "s" : ""} ($${r.cat}${cats > 1 ? ` + $${r.cat_additional}/extra` : ""})`);
    }
    return { total, note: `1 visit · ${parts.join(" + ")}` };
  }, [service, form.start_date, form.end_date, form.start_time, form.end_time, form.walk_length, form.drop_days, form.dogs_count, form.cats_count]);

  if (reference) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="flex min-h-[80vh] flex-col items-center justify-center px-6 py-24 text-center">
          <motion.span
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-20 w-20 items-center justify-center rounded-full bg-forest/10"
          >
            <Check className="h-9 w-9 text-forest" />
          </motion.span>
          <h1 className="mt-8 font-display text-5xl font-medium tracking-tight text-ink md:text-6xl">
            Request <em className="text-marmalade">received.</em>
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-inkSoft">
            Ari will check her calendar and confirm by email within a few hours.
          </p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-marmalade">
            Save your reference — you'll need it to pay
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
            <span
              data-testid="booking-reference-chip"
              className="rounded-full border border-line bg-white px-7 py-3 font-mono text-sm tracking-[0.25em] text-ink"
            >
              {reference}
            </span>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(reference);
                  toast.success("Reference copied — save it somewhere safe!");
                } catch {
                  toast.error("Copy didn't work — please write it down: " + reference);
                }
              }}
              data-testid="success-copy-ref-btn"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-colors duration-200 hover:bg-sand"
            >
              <Copy className="h-4 w-4 text-marmalade" />
              Copy
            </button>
          </div>
          {result?.price_total ? (
            <p data-testid="success-price" className="mt-5 text-base text-ink">
              <span className="font-display text-2xl font-semibold">${Number(result.price_total).toFixed(2)}</span>
              <span className="ml-2 text-inkSoft"> · {result.price_note}</span>
            </p>
          ) : null}
          <Link
            to={`/pay?ref=${reference}`}
            data-testid="success-pay-btn"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink"
          >
            Go to my payment page
          </Link>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-inkSoft">
            Bookmark it! You can always reach the pay page from the footer — payment opens the moment Ari
            approves, by card, Apple Pay, Venmo or Zelle.
          </p>
          <Link
            to={`/messages?ref=${reference}`}
            data-testid="success-message-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:bg-sand"
          >
            Have a question? Message Ari about this request
          </Link>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/"
              data-testid="success-home-btn"
              className="rounded-full bg-forest px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink"
            >
              Back home
            </Link>
            <button
              onClick={reset}
              data-testid="success-again-btn"
              className="rounded-full border border-ink/15 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-[background-color] duration-300 hover:bg-sand"
            >
              Book another
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const dateText = () => {
    if (service?.slug === "drop-in-visits") {
      const days = form.drop_days.filter((d) => d.date);
      return `${days.length} day${days.length !== 1 ? "s" : ""} — ` + days.map((d) => `${d.date}: ${d.slot_1} & ${d.slot_2}${d.third ? ` & ${d.slot_3} (3rd)` : ""}`).join(" | ");
    }
    if (service?.overnight) {
      return `${form.start_date} → ${form.end_date} · drop-off ${form.start_time} · pick-up ${form.end_time}`;
    }
    const walk = service?.slug === "dog-walking" && form.walk_length ? ` · ${form.walk_length} walk` : "";
    return `${form.start_date} · ${form.time_slot}${walk}`;
  };

  const petsLabel = () => {
    const parts = [];
    if (form.dogs_count) parts.push(`${form.dogs_count} dog${form.dogs_count > 1 ? "s" : ""}`);
    if (form.cats_count) parts.push(`${form.cats_count} cat${form.cats_count > 1 ? "s" : ""}`);
    return parts.join(" · ");
  };

  const reviewRows = [
    ["Service", service?.name],
    ["When", (form.start_date || (service?.slug === "drop-in-visits" && form.drop_days?.some((d) => d.date))) ? dateText() : ""],
    ["Pets", form.pet_name ? `${form.pet_name}${form.pet_age ? ` (${form.pet_age})` : ""} — ${petsLabel()}` : ""],
    ["Care notes", form.care_instructions],
    ["Owner", `${form.owner_name} · ${form.owner_email} · ${form.owner_phone}${form.owner_zip ? ` · ${form.owner_zip}` : ""}`],
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="pt-28 md:pt-36">
        <div className="mx-auto max-w-[1080px] px-6 pb-28 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">Booking request</p>
          <h1 className="mt-4 font-display text-5xl font-medium leading-[0.98] tracking-tight text-ink md:text-7xl">
            <MaskedLines lines={[<span key="a">Tell us about</span>, <span key="b">your <em className="text-marmalade">stay.</em></span>]} />
          </h1>

          <div className="mt-12 flex items-center gap-0" data-testid="booking-progress">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex flex-1 items-center last:flex-none">
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full border font-mono text-xs transition-colors duration-300 ${
                      i < step
                        ? "border-forest bg-forest text-cream"
                        : i === step
                        ? "border-marmalade bg-marmalade text-white"
                        : "border-line bg-white text-inkSoft"
                    }`}
                  >
                    {i < step ? <Check className="h-4 w-4" /> : `0${i + 1}`}
                  </span>
                  <span
                    className={`hidden font-mono text-[10px] uppercase tracking-[0.2em] md:block ${
                      i === step ? "text-ink" : "text-inkSoft"
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {i < STEP_LABELS.length - 1 && <div className={`mx-4 h-px flex-1 ${i < step ? "bg-forest" : "bg-line"}`} />}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 44 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -44 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12"
            >
              {step === 0 && (
                <div data-testid="booking-step-service">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {services === null && <p className="text-inkSoft">Loading services…</p>}
                    {services?.length === 0 && <p className="text-inkSoft">Services are on their way — check back soon.</p>}
                    {services?.map((s) => {
                      const Icon = SERVICE_ICONS[s.slug] || PawPrint;
                      const active = form.service_slug === s.slug;
                      return (
                        <button
                          key={s.slug}
                          onClick={() => pickService(s)}
                          data-testid={`book-service-card-${s.slug}`}
                          className={`flex items-start gap-4 rounded-3xl border p-6 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${
                            active ? "border-marmalade bg-white shadow-[0_10px_30px_rgba(217,108,74,0.14)]" : "border-line bg-white hover:border-ink/25"
                          }`}
                        >
                          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${active ? "bg-marmalade/15" : "bg-sand"}`}>
                            <Icon className={`h-5 w-5 ${active ? "text-marmalade" : "text-forest"}`} />
                          </span>
                          <span>
                            <span className="block font-display text-2xl font-medium text-ink">{s.name}</span>
                            <span className="mt-1 block text-sm text-inkSoft">{s.tagline}</span>
                            <span className="mt-3 block font-mono text-[10px] uppercase tracking-[0.18em] text-marmalade">
                              {s.price} · {s.duration}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {errors.service_slug && <p data-testid="book-service-error" className="mt-4 text-xs text-red-600">{errors.service_slug}</p>}
                </div>
              )}

              {step === 1 && (
                <div data-testid="booking-step-schedule" className="rounded-3xl border border-line bg-white p-8 md:p-10">
                  {service?.overnight ? (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <Field label="First day" error={errors.start_date} testid="book-start-date">
                        <input
                          type="date"
                          min={todayStr()}
                          value={form.start_date}
                          onChange={(e) => set("start_date", e.target.value)}
                          data-testid="book-start-date-input"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Last day" error={errors.end_date} testid="book-end-date">
                        <input
                          type="date"
                          min={form.start_date || todayStr()}
                          value={form.end_date}
                          onChange={(e) => set("end_date", e.target.value)}
                          data-testid="book-end-date-input"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Drop-off time (first day)" testid="book-start-time">
                        <input
                          type="time"
                          value={form.start_time}
                          onChange={(e) => set("start_time", e.target.value)}
                          data-testid="book-start-time-input"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Pick-up time (last day)" testid="book-end-time">
                        <input
                          type="time"
                          value={form.end_time}
                          onChange={(e) => set("end_time", e.target.value)}
                          data-testid="book-end-time-input"
                          className={inputCls}
                        />
                      </Field>
                      <p className="text-xs leading-relaxed text-inkSoft sm:col-span-2">
                        Pick-up at or before 2:30 PM on the last day? That last day is half price — perfect for
                        early goodbyes.
                      </p>
                    </div>
                  ) : service?.slug === "drop-in-visits" ? (
                    <div className="space-y-4">
                      {form.drop_days.map((d, i) => (
                        <div key={i} className="space-y-4 rounded-2xl border border-line bg-white/70 p-5">
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <Field label={i === 0 ? "Day" : `Day ${i + 1}`} error={errors[`drop_date_${i}`]} testid={`book-drop-date-${i}`}>
                              <input
                                type="date"
                                min={todayStr()}
                                value={d.date}
                                onChange={(e) => setDropDay(i, "date", e.target.value)}
                                data-testid={`book-drop-date-input-${i}`}
                                className={inputCls}
                              />
                            </Field>
                            <Field label="Visit 1 window" error={errors[`drop_v1_${i}`]} testid={`book-drop-v1-${i}`}>
                              <select
                                value={d.slot_1}
                                onChange={(e) => setDropDay(i, "slot_1", e.target.value)}
                                data-testid={`book-drop-v1-input-${i}`}
                                className={inputCls}
                              >
                                <option value="">Choose a window…</option>
                                {TIME_SLOTS.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                            </Field>
                            <Field label="Visit 2 window" error={errors[`drop_v2_${i}`]} testid={`book-drop-v2-${i}`}>
                              <select
                                value={d.slot_2}
                                onChange={(e) => setDropDay(i, "slot_2", e.target.value)}
                                data-testid={`book-drop-v2-input-${i}`}
                                className={inputCls}
                              >
                                <option value="">Choose a window…</option>
                                {TIME_SLOTS.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          </div>
                          <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-ink">
                              <input
                                type="checkbox"
                                checked={d.third}
                                onChange={(e) => setDropDay(i, "third", e.target.checked)}
                                data-testid={`book-drop-third-${i}`}
                                className="h-4 w-4 accent-[#3e5747]"
                              />
                              3rd visit (+$10)
                            </label>
                            {d.third && (
                              <div className="min-w-[220px] flex-1">
                                <Field label="Visit 3 window" error={errors[`drop_v3_${i}`]} testid={`book-drop-v3-${i}`}>
                                  <select
                                    value={d.slot_3}
                                    onChange={(e) => setDropDay(i, "slot_3", e.target.value)}
                                    data-testid={`book-drop-v3-input-${i}`}
                                    className={inputCls}
                                  >
                                    <option value="">Choose a window…</option>
                                    {TIME_SLOTS.map((t) => (
                                      <option key={t} value={t}>
                                        {t}
                                      </option>
                                    ))}
                                  </select>
                                </Field>
                              </div>
                            )}
                            {form.drop_days.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeDropDay(i)}
                                aria-label={`Remove day ${i + 1}`}
                                data-testid={`book-drop-remove-${i}`}
                                className="rounded-full p-2 text-inkSoft transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <button
                          type="button"
                          onClick={addDropDay}
                          data-testid="book-add-drop-day"
                          className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-colors duration-200 hover:bg-sand"
                        >
                          + Add another day
                        </button>
                        <p className="text-xs leading-relaxed text-inkSoft">
                          Each day includes two drop-in visits, each with its own window — Ari checks in, feeds,
                          plays, and tidies. Need a third visit on a day? Tick it for +$10 and pick its window.
                        </p>
                      </div>
                      {errors.start_date && (
                        <p data-testid="book-drop-days-error" className="text-xs text-red-600">{errors.start_date}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Field label="Day" error={errors.start_date} testid="book-date">
                          <input
                            type="date"
                            min={todayStr()}
                            value={form.start_date}
                            onChange={(e) => set("start_date", e.target.value)}
                            data-testid="book-date-input"
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Time window" error={errors.time_slot} testid="book-time-slot">
                          <select
                            value={form.time_slot}
                            onChange={(e) => set("time_slot", e.target.value)}
                            data-testid="book-time-slot-input"
                            className={inputCls}
                          >
                            <option value="">Choose a window…</option>
                            {TIME_SLOTS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      {service?.slug === "dog-walking" && (
                        <div className="mt-6">
                          <Field label="Walk length" error={errors.walk_length} testid="book-walk-length">
                            <div className="grid grid-cols-2 gap-4">
                              {[
                                { v: "30 min", label: "30 minutes", price: "25" },
                                { v: "60 min", label: "60 minutes", price: "40" },
                              ].map((o) => (
                                <button
                                  key={o.v}
                                  type="button"
                                  onClick={() => set("walk_length", o.v)}
                                  data-testid={`book-walk-length-${o.v.replace(" ", "-").toLowerCase()}`}
                                  className={`rounded-2xl border p-4 text-left transition-[border-color,background-color] duration-200 ${
                                    form.walk_length === o.v
                                      ? "border-marmalade bg-white shadow-[0_10px_30px_rgba(217,108,74,0.14)]"
                                      : "border-line bg-white hover:border-ink/25"
                                  }`}
                                >
                                  <span className="block text-sm font-semibold text-ink">{o.label}</span>
                                  <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-marmalade">
                                    ${o.price} · +$5 extra dog
                                  </span>
                                </button>
                              ))}
                            </div>
                          </Field>
                        </div>
                      )}
                    </>
                  )}
                  <p className="mt-6 text-sm text-inkSoft">
                    {service?.overnight
                      ? "Ari stays in your home overnight — arrival by evening, departure after the morning routine."
                      : "Ari will arrive within your chosen window and send a photo right after the visit."}
                  </p>
                </div>
              )}

              {step === 2 && (
                <div data-testid="booking-step-greet" className="rounded-3xl border border-line bg-white p-8 md:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marmalade">Free meet & greet</p>
                  <h2 className="mt-3 font-display text-3xl font-medium text-ink md:text-4xl">Say hello before the stay</h2>
                  <p className="mt-3 max-w-lg text-sm leading-relaxed text-inkSoft">
                    Ari likes to meet every pet first. Share your top three dates and times for a short meet &
                    greet — she'll confirm one of them together with your booking.
                  </p>
                  <div className="mt-8 space-y-6">
                    {form.greet_slots.map((slot, i) => (
                      <div key={i} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label={`Choice ${i + 1} — date`} testid={`book-greet-date-${i + 1}`}>
                          <input
                            type="date"
                            min={todayStr()}
                            value={slot.date}
                            onChange={(e) => setGreet(i, "date", e.target.value)}
                            data-testid={`book-greet-date-${i + 1}-input`}
                            className={inputCls}
                          />
                        </Field>
                        <Field label={`Choice ${i + 1} — time`} testid={`book-greet-time-${i + 1}`}>
                          <input
                            type="time"
                            value={slot.time}
                            onChange={(e) => setGreet(i, "time", e.target.value)}
                            data-testid={`book-greet-time-${i + 1}-input`}
                            className={inputCls}
                          />
                        </Field>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-sm text-inkSoft">Optional — but Ari recommends it, especially for nervous pets.</p>
                </div>
              )}

              {step === 3 && (
                <div data-testid="booking-step-pet" className="rounded-3xl border border-line bg-white p-8 md:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marmalade">Your pets</p>
                  <h2 className="mt-3 font-display text-3xl font-medium text-ink md:text-4xl">Who's staying with Ari?</h2>
                  <p className="mt-3 text-sm leading-relaxed text-inkSoft">
                    Puppies, seniors, reactive dogs, big dogs, cats — Ari welcomes them all.
                  </p>
                  <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Field label="How many dogs?" error={errors.dogs_count} testid="book-dogs-count">
                      <select
                        value={form.dogs_count}
                        onChange={(e) => set("dogs_count", Number(e.target.value))}
                        data-testid="book-dogs-count-input"
                        className={inputCls}
                      >
                        {(service?.slug === "dog-walking" ? [1, 2, 3, 4] : [0, 1, 2, 3, 4]).map((n) => (
                          <option key={n} value={n}>
                            {n} {n === 1 ? "dog" : "dogs"}
                          </option>
                        ))}
                      </select>
                    </Field>
                    {service?.slug === "dog-walking" ? (
                      <div className="flex items-end">
                        <p className="w-full rounded-2xl bg-sand p-4 text-sm leading-relaxed text-inkSoft">
                          Walks are for dogs — for cats, book a drop-in visit instead.
                        </p>
                      </div>
                    ) : (
                      <Field label="How many cats?" testid="book-cats-count">
                        <select
                          value={form.cats_count}
                          onChange={(e) => set("cats_count", Number(e.target.value))}
                          data-testid="book-cats-count-input"
                          className={inputCls}
                        >
                          {[0, 1, 2, 3, 4].map((n) => (
                            <option key={n} value={n}>
                              {n} {n === 1 ? "cat" : "cats"}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}
                    <Field label="Pet name(s)" error={errors.pet_name} testid="book-pet-name">
                      <input
                        value={form.pet_name}
                        onChange={(e) => set("pet_name", e.target.value)}
                        placeholder="Biscuit & Waffles"
                        data-testid="book-pet-name-input"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Breed / size (optional)" testid="book-pet-breed">
                      <input
                        value={form.pet_breed}
                        onChange={(e) => set("pet_breed", e.target.value)}
                        placeholder="Golden retriever, 30 kg"
                        data-testid="book-pet-breed-input"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Age (optional)" testid="book-pet-age">
                      <input
                        value={form.pet_age}
                        onChange={(e) => set("pet_age", e.target.value)}
                        placeholder="4 years"
                        data-testid="book-pet-age-input"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="mt-6 rounded-2xl border border-line bg-white/60 p-5">
                    <div className="flex flex-wrap items-center gap-5">
                      <span className="relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-sand ring-2 ring-marmalade ring-offset-2 ring-offset-cream">
                        {form.pet_photo ? (
                          <img src={form.pet_photo} alt="Your pet" className="h-full w-full object-cover" data-testid="pet-photo-preview" />
                        ) : (
                          <PawPrint className="h-7 w-7 text-marmalade" />
                        )}
                      </span>
                      <div className="min-w-[200px] flex-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">A photo of your pet (optional)</p>
                        <p className="mt-1 text-xs leading-relaxed text-inkSoft">
                          Helps Ari say hello before she arrives. JPEG, PNG, WebP or GIF — up to 8 MB.
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <label className="cursor-pointer">
                            <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-colors duration-200 hover:bg-sand">
                              <ImagePlus className="h-4 w-4 text-marmalade" />
                              {petUploading ? "Uploading…" : form.pet_photo ? "Change photo" : "Choose a photo"}
                            </span>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={(e) => pickPetPhoto(e.target.files?.[0])}
                              data-testid="book-pet-photo-input"
                              className="hidden"
                            />
                          </label>
                          {form.pet_photo && !petUploading && (
                            <button
                              type="button"
                              onClick={() => set("pet_photo", "")}
                              aria-label="Remove pet photo"
                              data-testid="book-pet-photo-remove"
                              className="rounded-full p-2 text-inkSoft transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Field label="Care instructions — routines, meds, quirks" error={errors.care_instructions} testid="book-care-instructions">
                      <textarea
                        value={form.care_instructions}
                        onChange={(e) => set("care_instructions", e.target.value)}
                        rows={5}
                        placeholder="Two feeds a day (7am / 6pm), one tablet hidden in cheese at dinner. She's shy with new people — let her come to you."
                        data-testid="book-care-instructions-input"
                        className={`${inputCls} resize-none leading-relaxed`}
                      />
                    </Field>
                  </div>
                  <div className="mt-10 border-t border-line pt-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marmalade">You</p>
                    <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                      <Field label="Name" error={errors.owner_name} testid="book-owner-name">
                        <input
                          value={form.owner_name}
                          onChange={(e) => set("owner_name", e.target.value)}
                          placeholder="Alex Rivera"
                          data-testid="book-owner-name-input"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Email" error={errors.owner_email} testid="book-owner-email">
                        <input
                          type="email"
                          value={form.owner_email}
                          onChange={(e) => set("owner_email", e.target.value)}
                          placeholder="alex@email.com"
                          data-testid="book-owner-email-input"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Phone" error={errors.owner_phone} testid="book-owner-phone">
                        <input
                          type="tel"
                          value={form.owner_phone}
                          onChange={(e) => set("owner_phone", e.target.value)}
                          placeholder="(555) 123-4567"
                          data-testid="book-owner-phone-input"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Home zip code" error={errors.owner_zip} testid="book-owner-zip">
                        <input
                          value={form.owner_zip}
                          onChange={(e) => onZipChange(e.target.value)}
                          placeholder="11201"
                          inputMode="numeric"
                          data-testid="book-owner-zip-input"
                          className={inputCls}
                        />
                        {zipCheck?.enabled && zipCheck.ok && (
                          <span data-testid="zip-check-ok" className="mt-1.5 block text-xs font-medium text-forest">
                            {zipCheck.message}
                          </span>
                        )}
                        {zipCheck?.enabled && !zipCheck.ok && (
                          <span data-testid="zip-check-far" className="mt-1.5 block text-xs font-medium text-red-600">
                            {zipCheck.message}
                          </span>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="booking-step-review" className="rounded-3xl border border-line bg-white p-8 md:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marmalade">Almost there</p>
                  <h2 className="mt-3 font-display text-3xl font-medium text-ink md:text-4xl">One last look</h2>
                  <dl className="mt-8 divide-y divide-line">
                    {reviewRows.map(([label, value]) => (
                      <div key={label} className="grid grid-cols-1 gap-1 py-4 sm:grid-cols-4 sm:gap-6">
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkSoft">{label}</dt>
                        <dd className="text-sm leading-relaxed text-ink sm:col-span-3">{value}</dd>
                      </div>
                    ))}
                    <div className="grid grid-cols-1 gap-1 py-4 sm:grid-cols-4 sm:gap-6">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkSoft">Meet & greet</dt>
                      <dd data-testid="review-greet-summary" className="text-sm leading-relaxed text-ink sm:col-span-3">
                        {form.greet_slots.filter((g) => g.date).length > 0
                          ? form.greet_slots
                              .filter((g) => g.date)
                              .map((g) => `${g.date}${g.time ? ` · ${g.time}` : ""}`)
                              .join("  ·  ")
                          : "Not requested yet"}
                      </dd>
                    </div>
                    <div className="grid grid-cols-1 gap-1 py-4 sm:grid-cols-4 sm:gap-6">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-marmalade">Estimated total</dt>
                      <dd data-testid="review-price-total" className="text-sm leading-relaxed text-ink sm:col-span-3">
                        <span className="font-display text-2xl font-semibold text-ink">${estimate.total.toFixed(2)}</span>
                        <span className="ml-2 text-inkSoft"> · {estimate.note}</span>
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-6 rounded-2xl bg-sand p-5 text-sm leading-relaxed text-inkSoft">
                    This is a request — Ari personally reviews every one and confirms by email within a few
                    hours. The total is the estimate for your stay; nothing is charged now.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {errors.form && (
            <p data-testid="booking-form-error" className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {errors.form}
            </p>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={back}
              disabled={step === 0}
              data-testid="book-back-btn"
              className={`inline-flex items-center gap-2 rounded-full border border-ink/15 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-[background-color,opacity] duration-300 hover:bg-sand ${
                step === 0 ? "cursor-not-allowed opacity-40" : ""
              }`}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            {step < 4 ? (
              <button
                onClick={next}
                data-testid="book-continue-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-marmalade px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-marmalade-deep"
              >
                Continue
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="book-submit-btn"
                className="inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ink disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send request"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Book;
